import React from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { useContentScrollFabBounds } from "@/hooks/useContentScrollFabBounds";

type DiffNavigationFabVariant = "warning" | "primary";

type DiffNavigationFabProps = {
  /** 0-based index of the currently highlighted change. */
  currentIndex: number;
  /** Total number of navigable changes respecting the active filter. */
  totalChanges: number;
  onPrevious: () => void;
  onNext: () => void;
  variant?: DiffNavigationFabVariant;
  /** When set, FAB horizontal position aligns to this element instead of the scroll area. */
  boundsElement?: HTMLElement | null;
  previousLabel?: string;
  nextLabel?: string;
  groupLabel?: string;
};

const variantClasses: Record<
  DiffNavigationFabVariant,
  { container: string; button: string; counter: string }
> = {
  warning: {
    container: "border-warning/50 dark:border-warning/80",
    button:
      "hover:bg-warning/20 dark:hover:bg-warning/30 text-warning",
    counter: "text-warning",
  },
  primary: {
    container: "border-primary/40 dark:border-primary/60",
    button:
      "hover:bg-primary/15 dark:hover:bg-primary/25 text-primary",
    counter: "text-primary",
  },
};

/**
 * Floating prev/next control for diff or consolidation changes, fixed near the
 * bottom-center of the viewport so it stays reachable without scrolling back
 * to the sticky top controls.
 */
const DiffNavigationFab: React.FC<DiffNavigationFabProps> = ({
  currentIndex,
  totalChanges,
  onPrevious,
  onNext,
  variant = "warning",
  boundsElement,
  previousLabel = "Forrige afsnit med ændring",
  nextLabel = "Næste afsnit med ændring",
  groupLabel = "Naviger mellem ændringer",
}) => {
  const bounds = useContentScrollFabBounds(boundsElement);
  const styles = variantClasses[variant];

  if (totalChanges <= 0) return null;

  return (
    <div
      className={cn(
        "no-print fixed bottom-6 z-40 -translate-x-1/2 flex items-center gap-1 rounded-full border bg-background px-2 py-1 shadow-lg transition-[left] duration-200 ease-in-out",
        styles.container,
        !bounds && "left-1/2"
      )}
      style={bounds ? { left: bounds.centerX } : undefined}
      role="group"
      aria-label={groupLabel}
    >
      <button
        type="button"
        className={cn(
          "p-1 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40",
          styles.button
        )}
        onClick={onPrevious}
        disabled={totalChanges === 0}
        aria-label={previousLabel}
      >
        <ChevronUp className="size-4" />
      </button>
      <span
        className={cn(
          "text-xs tabular-nums min-w-12 text-center select-none",
          styles.counter
        )}
      >
        {currentIndex + 1}/{totalChanges}
      </span>
      <button
        type="button"
        className={cn(
          "p-1 rounded-full transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-40",
          styles.button
        )}
        onClick={onNext}
        disabled={totalChanges === 0}
        aria-label={nextLabel}
      >
        <ChevronDown className="size-4" />
      </button>
    </div>
  );
};

export default DiffNavigationFab;
